/*
 * Copyright 2020 Thomer Gil [https://thomer.com/games/brickbreaker-js/]
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
// {{{ imports
import * as ex from 'excalibur';
import { Game } from './game';
import { Ball } from './ball';
import { BB } from './globals';
// }}}

export class Paddle extends ex.Actor {
    // {{{ members, constructors
    private touchMove: string;
    private _move: number;

    private _autoAimXPercDiff: number;
    private _autoAimXPercDiffCounter: number;
    private _autoAimAtPaddlePerc: number;
    private _autoPrevPaddleAimXPerc: number;

    constructor(vMult?: number) {
        super({
            width: BB.PADDLE_WIDTH,
            height: BB.PADDLE_HEIGHT,
            color: BB.PADDLE_COLOR,
            collisionType: ex.CollisionType.Fixed,
        });
        this.reset();
        this.setVMult(vMult);
        this.touchMove = null;

        // a ball bounced against us
        this.on('precollision', (ev) => {
            // In Excalibur 0.30, ev.other is the collider, not the actor
            const otherActor = ev.other.owner;
            if (otherActor instanceof Ball) {
                this.ballBounce(otherActor);
            }
        });

        // handle touch
        // (() => {} notation has no `this` context, which mean we can refer to
        // this.startMoving(), with this being the current Paddle)
        Game.Instance().input.pointers.primary.on('down', (ev) => {
            if (ev.screenPos.x > Game.Instance().halfDrawWidth) {
                this.startMoving('right');
            } else if (ev.screenPos.x < Game.Instance().halfDrawWidth) {
                this.startMoving('left');
            }
        });

        Game.Instance().input.pointers.primary.on('up', () => {
            this.stopMoving();
        });
    }
    // }}}

    // {{{ public

    //
    // determines the ball's behavior depending on:
    //
    // 1) what direction the ball is coming from
    // 2) what speed the the ball has
    // 3) where on the paddle it landed
    //
    //                      \
    //                       \
    //                        \
    //                         O
    // ---------------------------------------------------
    // | zone 1 |        zone 2          |    zone 3     |
    // ---------------------------------------------------
    //
    // 0%        25%        50%           75%         100%
    //
    // In the above picture, the ball is coming from left to right
    // (ball.left_to_right() == True) with a horizontal velocity of
    // ball.xvelocity() and it lands on zone 2 of the paddle.
    //
    // The paddle's zones are defined in PADDLE_ZONES. PADDLE_ZONES is a
    // 2-tier hash table, first keyed by the velocity of the incoming
    // ball, and secondly by the percentage away from the left edge. (Or,
    // when the ball is coming from the right, the percentage away from
    // the right edge.)
    //
    // Determine outgoing horizontal velocity as follows.
    //
    // 1. Look up PADDLE_ZONES[ball.xvelocity()]. (That is, look up
    // the behavior given that ball's current horizontal velocity.)
    //
    // 2. Determine which zone of the paddle the ball landed on by
    // going through the paddle's zones (as defined in the table that
    // emerged from the previous step), from left to right.
    //
    // 3. Either set ("SET" in PADDLE_ZONES) the ball's new horizontal
    // velocity, or multiply ("MULT" in PADDLE_ZONES) the current ball's
    // horizontal velocity.
    //
    public ballBounce(ball: Ball): void {
        // NB: yuck. prevents multiple calls for a single bounce.
        if (ball.pos.y < this.pos.y - BB.BALL_RADIUS * 2) {
            return;
        }

        // compute the distance (in %'s) from the paddle's edge
        const percFromEdge = this._percFromEdge(ball.pos.x, ball.rightToLeft());

        // find the paddle zone the ball fell into and retrieve the
        // action we are supposed to take
        const action = this._dist2action(percFromEdge, ball.xvelocity());

        // handle vertical bounce; this always happens when bouncing
        // against the paddle
        ball.vbounce();

        // handle horizontal bounce based on the action
        if (action[1] == 'MULT') {
            ball.setXVelocity(ball.xvelocity() * action[2]);
        } else if (action[1] == 'SET') {
            const r2l = ball.rightToLeft();
            ball.setXVelocity(action[2]);
            if (r2l) {
                ball.hbounce(); // invert direction, because action[2] was a left-to-right
            }
        } else {
            console.log('undefined action = ' + action[1]);
        }

        // NB: yuck. we need to explicitly place the ball *above* the paddle to
        // prevent multiple bounce events
        //
        // make sure that the ball is firmly above the paddle and didn't
        // accidentally "sink into" the paddle due to a rounding error
        // self.ball.rect.y = self.paddle.rect.y - 2*BALL_RADIUS
        ball.pos.y = this.pos.y - BB.BALL_RADIUS * 2 - 1;

        // let the game know that a ball/paddle bounce occurred
        this.emit('bounce', null);

        // reset where we let the ball bounce next in auto mode
        this._autoAimAtPaddlePerc = null;
    }

    //
    // auto-moves the paddle
    //
    // Decides randomly where on the paddle to let the ball bounce
    // (self.autoPaddleAimXPerc) and then smoothly moves the paddle along while adjusting
    //
    public autoMove(): void {
        //
        // choose a new autoPaddleAimXPerc; this is right after a ball/paddle bounce
        //
        // autoPaddleAimXPerc is where on the paddle (in % from the left edge) we're aiming the ball
        // autoPrevPaddleAimXPerc is where on the paddle the ball was aimed when we chose a new autoPaddleAimXPerc.
        // autoAimXPercDiff is the difference between those two
        // autoAimXPercDiffCounter is used to slowly go from 0 to autoAimXPercDiff
        //
        const ball = Game.Instance().autoFollowBall();
        if (!this._autoAimAtPaddlePerc) {
            this._autoAimAtPaddlePerc = Math.random() * (98 - 2) + 2;
            this._autoPrevPaddleAimXPerc =
                ((ball.getx() - BB.BALL_RADIUS - (this.pos.x - BB.PADDLE_WIDTH / 2)) / BB.PADDLE_WIDTH) * 100;
            // ball left side x coordinate     // padde left side x coordinate
            this._autoAimXPercDiff = this._autoAimAtPaddlePerc - this._autoPrevPaddleAimXPerc;
            this._autoAimXPercDiffCounter = 0.0;
        }

        // compute where the paddle should move based on autoPrevPaddleAimXPerc
        // compute where the paddle should move based on autoPaddleAimXPerc
        // compute where the paddle should move to slowly get from autoPrevPaddleAimXPerc to autoPaddleAimXPerc
        const prevGotoX =
            ball.getx() - BB.BALL_RADIUS - (BB.PADDLE_WIDTH / 100) * this._autoPrevPaddleAimXPerc + BB.PADDLE_WIDTH / 2;
        const nowGotoX = prevGotoX - (BB.PADDLE_WIDTH / 100) * this._autoAimXPercDiffCounter + BB.PADDLE_WIDTH / 2;

        // get autoAimXPercDiffCounter a bit closer to autoAimXPercDiff
        if (this._autoAimXPercDiff < 0 && this._autoAimXPercDiffCounter > this._autoAimXPercDiff) {
            this._autoAimXPercDiffCounter -= 0.25;
        } else if (this._autoAimXPercDiff > 0 && this._autoAimXPercDiffCounter < this._autoAimXPercDiff) {
            this._autoAimXPercDiffCounter += 0.25;
        }

        // move paddle
        this.pos.x = nowGotoX - BB.PADDLE_WIDTH / 2;
    }
    public setVMult(vMult: number): void {
        this._move = Math.min(BB.PADDLE_MOVE * vMult, BB.MAX_PADDLE_MOVE);
    }

    // sets paddle back in the middle and clears input state
    public reset(): void {
        this.pos = new ex.Vector(
            Game.Instance().halfDrawWidth,
            Game.Instance().drawHeight - BB.BELOW_PADDLE_EMPTY_SPACE - BB.PADDLE_HEIGHT,
        );
        this._autoAimAtPaddlePerc = null;
        this.touchMove = null;

        // Clear any stuck keyboard state by triggering key up events
        const keyboard = Game.Instance().input.keyboard;
        if (keyboard.isHeld(ex.Keys.Left)) {
            keyboard.triggerEvent('up', ex.Keys.Left);
        }
        if (keyboard.isHeld(ex.Keys.Right)) {
            keyboard.triggerEvent('up', ex.Keys.Right);
        }
        if (keyboard.isHeld(ex.Keys.ShiftLeft)) {
            keyboard.triggerEvent('up', ex.Keys.ShiftLeft);
        }
        if (keyboard.isHeld(ex.Keys.ShiftRight)) {
            keyboard.triggerEvent('up', ex.Keys.ShiftRight);
        }
    }

    public startMoving(direction: string): void {
        this.touchMove = direction;
    }

    public stopMoving(): void {
        this.touchMove = null;
    }

    // handles moves, possible with shift
    public update(engine, delta): void {
        super.update(engine, delta);

        if (BB.PADDLE_AUTO) {
            this.autoMove();
        } else {
            let move = this._move;
            if (
                Game.Instance().input.keyboard.isHeld(ex.Keys.ShiftLeft) ||
                Game.Instance().input.keyboard.isHeld(ex.Keys.ShiftRight)
            ) {
                move = BB.PADDLE_SLOW_MOVE;
            }

            if (Game.Instance().input.keyboard.isHeld(ex.Keys.Left) || this.touchMove == 'left') {
                this.pos = new ex.Vector(this.pos.x - move, this.pos.y);
            }

            if (Game.Instance().input.keyboard.isHeld(ex.Keys.Right) || this.touchMove == 'right') {
                this.pos = new ex.Vector(this.pos.x + move, this.pos.y);
            }
        }
        this._edgeCorrect();
    }
    // }}}

    // {{{ private
    private _edgeCorrect(): void {
        if (this.pos.x - BB.PADDLE_WIDTH / 2 < 0) {
            this.pos.x = BB.PADDLE_WIDTH / 2;
        } else if (this.pos.x - BB.PADDLE_WIDTH / 2 > Game.Instance().drawWidth - BB.PADDLE_WIDTH) {
            this.pos.x = Game.Instance().drawWidth - BB.PADDLE_WIDTH / 2;
        }
    }

    // given an x coordinate and direction, compute how far from the
    // paddle's edge the ball hit (in %).
    private _percFromEdge(x, fromRightEdge = true): number {
        let p = ((x - (this.pos.x - BB.PADDLE_WIDTH / 2)) / BB.PADDLE_WIDTH) * 100;
        if (p < 0) {
            p = 0;
        } else if (p > 100) {
            p = 100;
        }

        // if we're asked to calculate from the right edge, invert it
        if (fromRightEdge) {
            p = 100 - p;
        }

        return p;
    }

    // determine which zone the paddle dropped into
    private _dist2action(perc: number, v: number): [number, string, number] {
        const zones = BB.PADDLE_ZONES[Math.abs(v)];
        let aggregatedPercentage = 0;
        for (const zone of zones) {
            if (aggregatedPercentage <= perc && perc <= aggregatedPercentage + zone[0]) {
                return zone;
            }
            aggregatedPercentage += zone[0];
        }
    }
    // }}}
}
